/**
 * u2netp segmentation adapter, backed by onnxruntime-web.
 *
 * Replicates the EXACT preprocessing used by src/app/docpic/cnn-classifier.ts
 * (and therefore the rembg/u2netp pipeline the trainer used):
 *   - 320² input named 'input.1',
 *   - divide each channel by the global max pixel value,
 *   - ImageNet normalise (mean .485/.456/.406, std .229/.224/.225),
 *   - output range-normalised to [0,1] (min-max over the mask),
 *   - upscaled (nearest) back to the source resolution.
 *
 * onnxruntime-web is a runtime dependency (not Angular), so this adapter is
 * browser/Node-portable. A Lambda may inject its own SegmentationModel instead.
 */
import * as ort from 'onnxruntime-web';
import { ImagePixels } from '../../core/ports';
import { SegmentationModel } from '../ports';
import { resizeImage } from '../core/image';

const SEG_SIZE = 320;
const MEAN = [0.485, 0.456, 0.406];
const STD = [0.229, 0.224, 0.225];

export interface U2netpOptions {
  /** URL/path to the u2netp ONNX model (default 'doc-seg.onnx', site root). */
  modelUrl?: string;
  /** Execution providers, e.g. ['webgpu','wasm']. Default ['wasm']. */
  executionProviders?: ('webgpu' | 'wasm' | 'cpu')[];
  /** Where onnxruntime-web should fetch its wasm binaries (browser). Without it
   * the wasm backend can't load. Leave unset in Node/Lambda. */
  wasmPaths?: string;
  /** Pre-created session (skips loading). */
  session?: ort.InferenceSession;
}

export class OnnxU2netp implements SegmentationModel {
  private session?: ort.InferenceSession;
  private loadPromise?: Promise<ort.InferenceSession>;
  private readonly modelUrl: string;
  private readonly eps: ('webgpu' | 'wasm' | 'cpu')[];
  private readonly wasmPaths?: string;

  constructor(opts: U2netpOptions = {}) {
    this.session = opts.session;
    this.modelUrl = opts.modelUrl ?? 'doc-seg.onnx';
    this.eps = opts.executionProviders ?? ['wasm'];
    this.wasmPaths = opts.wasmPaths;
  }

  private load(): Promise<ort.InferenceSession> {
    if (this.session) return Promise.resolve(this.session);
    if (this.wasmPaths) {
      ort.env.wasm.wasmPaths = this.wasmPaths;
      ort.env.wasm.numThreads = 1; // avoid the cross-origin-isolation requirement
    }
    this.loadPromise ??= ort.InferenceSession.create(this.modelUrl, {
      executionProviders: this.eps,
    }).then((s) => (this.session = s));
    return this.loadPromise;
  }

  async alpha(img: ImagePixels): Promise<Float32Array> {
    const session = await this.load();
    const small = resizeImage(img, SEG_SIZE, SEG_SIZE);
    const sd = small.data;
    let mx = 1;
    for (let i = 0; i < sd.length; i += 4) {
      if (sd[i] > mx) mx = sd[i];
      if (sd[i + 1] > mx) mx = sd[i + 1];
      if (sd[i + 2] > mx) mx = sd[i + 2];
    }
    const plane = SEG_SIZE * SEG_SIZE;
    const inp = new Float32Array(3 * plane);
    for (let i = 0; i < plane; i++) {
      inp[i] = (sd[i * 4] / mx - MEAN[0]) / STD[0];
      inp[plane + i] = (sd[i * 4 + 1] / mx - MEAN[1]) / STD[1];
      inp[2 * plane + i] = (sd[i * 4 + 2] / mx - MEAN[2]) / STD[2];
    }
    const out = await session.run({
      'input.1': new ort.Tensor('float32', inp, [1, 3, SEG_SIZE, SEG_SIZE]),
    });
    const mask = out[session.outputNames[0]].data as Float32Array;

    // Min-max normalise the 320² mask, then upscale (bilinear) to full res.
    let lo = Infinity, hi = -Infinity;
    for (const v of mask) {
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    const rng = hi - lo || 1;
    const small320: ImagePixels = {
      data: new Uint8ClampedArray(plane * 4),
      width: SEG_SIZE,
      height: SEG_SIZE,
    };
    for (let i = 0; i < plane; i++) {
      const a = ((mask[i] - lo) / rng) * 255;
      small320.data[i * 4] = small320.data[i * 4 + 1] = small320.data[i * 4 + 2] = a;
      small320.data[i * 4 + 3] = 255;
    }
    const up = resizeImage(small320, img.width, img.height);
    const alpha = new Float32Array(img.width * img.height);
    for (let i = 0; i < alpha.length; i++) alpha[i] = up.data[i * 4] / 255;
    return alpha;
  }
}
